import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
// Task 7 (2026-06-19) — shared strict-view role-home resolver.
import { resolveVedaManagerHome } from './lib/vedaManagerRoles';
import { VedaPurchaseOrderGuard } from './pages/veda-agro/VedaPurchaseOrderGuard';
import { VedaRenegotiationGuard } from './pages/veda-agro/VedaRenegotiationGuard';

// Role-aware default home: redirects strict-view roles AWAY from /dashboard.
// May 26, 2026 — Lead Triage Coordinator (Deepika) lands on /leads/triage.
// Task 7  — sales_manager → /veda-agro/sales, logistics_manager → /veda-agro/logistics.
function RoleAwareHome() {
  const { user } = useAuthStore.getState();
  const redirect = resolveVedaManagerHome((user?.role?.name ?? '').toLowerCase());
  if (redirect) return <Navigate to={redirect} replace />;
  return <Navigate to="/dashboard" replace />;
}

// Role-aware dashboard guard: same redirect even if a strict-view user types
// /dashboard directly into the URL.
function RoleAwareDashboard() {
  const { user } = useAuthStore.getState();
  const redirect = resolveVedaManagerHome((user?.role?.name ?? '').toLowerCase());
  if (redirect) return <Navigate to={redirect} replace />;
  return <DashboardPage />;
}
// CRM
import { ContactsPage } from './pages/crm/ContactsPage';
import { ContactDetailPage } from './pages/crm/ContactDetailPage';
import { AccountsPage } from './pages/crm/AccountsPage';
import { DealsPage } from './pages/crm/DealsPage';
import { DealDetailPage } from './pages/crm/DealDetailPage';
// Sales
import { ProposalsPage } from './pages/sales/ProposalsPage';
import { ProposalEditorPage } from './pages/sales/ProposalEditorPage';
import { DiscountRequestsPage } from './pages/sales/DiscountRequestsPage';
import { EstimatesPage } from './pages/sales/EstimatesPage';
import { EstimateEditorPage } from './pages/sales/EstimateEditorPage';
import { SalesOrdersPage } from './pages/sales/SalesOrdersPage';
import { SalesOrderEditorPage } from './pages/sales/SalesOrderEditorPage';
import { SalesOrderDetailPage } from './pages/sales/SalesOrderDetailPage';
import { SalesOrderFulfilmentPage } from './pages/sales/SalesOrderFulfilmentPage';
import { AccountDetailPage } from './pages/crm/AccountDetailPage';
// Sales — generic (renamed from VedaAgro-prefixed Apr 22 2026, Phase P2)
import { FinancialApprovalPage } from './pages/sales/FinancialApprovalPage';
import { FinancialApprovalsListPage } from './pages/sales/FinancialApprovalsListPage';
import { SecurityPaymentPage } from './pages/sales/SecurityPaymentPage';
import { SamplesPage } from './pages/sales/SamplesPage';
import { LandedCostPage } from './pages/sales/LandedCostPage';
import { DealClosurePage } from './pages/sales/DealClosurePage';
// Finance
import { InvoicesPage } from './pages/finance/InvoicesPage';
import { InvoiceEditorPage } from './pages/finance/InvoiceEditorPage';
import { ExpensesPage } from './pages/finance/ExpensesPage';
import { PaymentsPage } from './pages/finance/PaymentsPage';
import { CreditNotesPage } from './pages/finance/CreditNotesPage';
import { LegacyInvoicesPage } from './pages/veda-agro/finance/LegacyInvoicesPage'; // AI-4voo
import { DebitNotesPage } from './pages/finance/DebitNotesPage';
import { InvoiceSwapPage } from './pages/finance/InvoiceSwapPage';
// Projects
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { TasksPage } from './pages/projects/TasksPage';
// Procurement
import { VendorsPage } from './pages/procurement/VendorsPage';
import { PurchaseOrdersPage } from './pages/procurement/PurchaseOrdersPage';
import { PurchaseOrderEditorPage } from './pages/procurement/PurchaseOrderEditorPage';
// Integrations — generic (renamed Apr 22 2026)
import { TallySyncPage } from './pages/integrations/TallySyncPage';
// Service
import { ServiceTicketsPage } from './pages/service/ServiceTicketsPage';
import { ServiceTicketDetailPage } from './pages/service/ServiceTicketDetailPage';
import { ComplaintIntakePage } from './pages/service/ComplaintIntakePage';
import { WarrantyVerificationPage } from './pages/service/WarrantyVerificationPage';
import { PreVisitChecklistPage } from './pages/service/PreVisitChecklistPage';
import { ProductDiagnosisPage } from './pages/service/ProductDiagnosisPage';
import { OnSiteExecutionPage } from './pages/service/OnSiteExecutionPage';
import { WarrantyClaimPage } from './pages/service/WarrantyClaimPage';
import { WarrantiesPage } from './pages/service/WarrantiesPage';
// Service & Warranty module (NEW — Apr 2026)
import { ServiceTicketCreatePage } from './pages/service/ServiceTicketCreatePage';
import { ComponentIssuePage } from './pages/service/ComponentIssuePage';
import { ServicePhotosPage } from './pages/service/ServicePhotosPage';
import { ServiceAuditPage } from './pages/service/ServiceAuditPage';
import { OtpVerifyPage } from './pages/service/OtpVerifyPage';
import { ProductUnitWarrantyHistoryPage } from './pages/service/ProductUnitWarrantyHistoryPage';
// Support (standalone flat FSM)
import { SupportTicketsPage } from './pages/support/SupportTicketsPage';
import { SupportTicketDetailPage } from './pages/support/SupportTicketDetailPage';
// Product docs admin
import { ProductDocsAdminPage } from './pages/products/ProductDocsAdminPage';
// HR
import { EmployeesPage } from './pages/hr/EmployeesPage';
import { LeavePage } from './pages/hr/LeavePage';
import { IncentivesPage } from './pages/hr/IncentivesPage';
// Automation
import { WorkflowsPage } from './pages/automation/WorkflowsPage';
// AI
import { AIAssistantPage } from './pages/ai/AIAssistantPage';
import { AIAgentsPage } from './pages/ai/AIAgentsPage';
import { APIIntegrationsPage } from './pages/ai/APIIntegrationsPage';
import CreditsPage from './pages/ai/CreditsPage';
import { ProposalBuilderPage } from './pages/sales/ProposalBuilderPage';
import { KnowledgeBasePage } from './pages/knowledge-base/KnowledgeBasePage';
// Telecaller
import { CallLogsPage } from './pages/telecaller/CallLogsPage';
import { ProposalTrackingPage } from './pages/telecaller/ProposalTrackingPage';
import { TeamPerformancePage } from './pages/telecaller/TeamPerformancePage';
import { TelecallerTodayPage } from './pages/telecaller/TelecallerTodayPage';
import { CallWorkspacePage } from './pages/telecaller/CallWorkspacePage';
// ChatKart Call Handoff — agent call history (M4, Apr 26 2026)
const MyCallsPage = lazy(() =>
  import('./pages/calls/MyCallsPage').then((m) => ({ default: m.MyCallsPage })),
);
// Profile
import { ProfilePage } from './pages/profile/ProfilePage';
// Settings
import { SettingsPage } from './pages/settings/SettingsPage';
import { BillingPlansPage } from './pages/settings/BillingPlansPage';
import { AIAssistantSettingsPage } from './pages/settings/AIAssistantSettingsPage';
// Sessions & Devices (2026-09-05) — one phone and one browser per person.
import { SessionsDevicesPage } from './pages/settings/SessionsDevicesPage';
import { UserDeviceHistoryPage } from './pages/settings/UserDeviceHistoryPage';
// Super Admin
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
import { TenantManagementPage } from './pages/admin/TenantManagementPage';
import { BillingPage } from './pages/admin/BillingPage';
import { SystemHealthPage } from './pages/admin/SystemHealthPage';
import { TeamPage } from './pages/admin/TeamPage';
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { LoginHistoryPage } from './pages/admin/LoginHistoryPage';
import { ResourceManagement } from './pages/admin/ResourceManagement';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { CarrierNumbersPage } from './pages/admin/CarrierNumbersPage';
import { InstallationsFocusPage } from './pages/admin/InstallationsFocusPage';
// Sun Stellar Pool Heat Pump Calculator (Track D, May 2026 — Ananya).
// Module-gated by `pool_heat_pump_calculator`; backend 404s the routes when
// the tenant flag isn't set, so listing them here is harmless for non–Sun-Stellar
// tenants. UI gate is on the sidebar entry (which uses tenant.enabledModules).
import { MeetingModeLauncher } from './pages/sales/MeetingMode/MeetingModeLauncher';
import { PoolCalculatorPage } from './pages/sales/MeetingMode/PoolCalculatorPage';
import { CalculatorChooserPage } from './pages/sales/MeetingMode/CalculatorChooserPage';
import { CommercialHeatPumpCalculatorPage } from './pages/sales/MeetingMode/CommercialHeatPumpCalculatorPage';
import { SsTankCalculatorPage } from './pages/sales/MeetingMode/SsTankCalculatorPage';
import { DomesticHeatPumpCalculatorPage } from './pages/sales/MeetingMode/DomesticHeatPumpCalculatorPage';
import { PoolModelsAdmin } from './pages/admin/PoolModelsAdmin';
import { CommercialHeatPumpModelsAdmin } from './pages/admin/CommercialHeatPumpModelsAdmin';
import { SsTankModelsAdmin } from './pages/admin/SsTankModelsAdmin';
import { DomesticHeatPumpModelsAdmin } from './pages/admin/DomesticHeatPumpModelsAdmin';
// Procurement Automation
import { QuotationJobsPage } from './pages/procurement/QuotationJobsPage';
import { QuotationJobDetailPage } from './pages/procurement/QuotationJobDetailPage';
// Procurement contracts
import { PurchaseContractsPage } from './pages/procurement/PurchaseContractsPage';
import { TransportContractsPage } from './pages/procurement/TransportContractsPage';
// Purchase Dashboard & Vendor Rate Comparison
import { PurchaseDashboardPage } from './pages/procurement/PurchaseDashboardPage';
import { VendorRateComparisonPage } from './pages/procurement/VendorRateComparisonPage';
// Service escalation
import { EscalationRulesPage } from './pages/service/EscalationRulesPage';
// Service SOP
import { ServiceSOPDashboardPage } from './pages/service/ServiceSOPDashboardPage';
import { ComplaintDetailPage } from './pages/service/ComplaintDetailPage';
// ITBP SWH Installation Flow (Apr 2026)
import { ITBPInstallationsPage } from './pages/service/ITBPInstallationsPage';
import { InstallationJobDetailPage } from './pages/service/InstallationJobDetailPage';
import { ITBPReportsPage } from './pages/service/ITBPReportsPage';
// Lead Integrations
import { LeadsPage } from './pages/crm/LeadsPage';
// Apr 26, 2026 (Squad-B) — Lead Manager workflow stubs
import { UnverifiedLeadsPage } from './pages/leads/UnverifiedLeadsPage';
import { VerifiedLeadsPage } from './pages/leads/VerifiedLeadsPage';
import { LeadApprovalsPage } from './pages/leads/LeadApprovalsPage';
// Phase 4 (2026-05-26) — Deepika's lead-triage dashboard
import LeadTriageDashboard from './pages/leads/LeadTriageDashboard';
import { AdminLeadApprovalsPage } from './pages/admin/AdminLeadApprovalsPage';
// Customers
import { CustomersPage } from './pages/crm/CustomersPage';
// SO-PO Orchestration
import { SoPoMappingPage } from './pages/sales/SoPoMappingPage';
import { MarginCalculatorPage } from './pages/sales/MarginCalculatorPage';
// Field Partners
import { FieldPartnerDirectoryPage } from './pages/field-partners/FieldPartnerDirectoryPage';
import { KycReviewPage } from './pages/field-partners/KycReviewPage';
import { InstallationClaimsPage } from './pages/field-partners/InstallationClaimsPage';
import { IncentiveRulesPage } from './pages/field-partners/IncentiveRulesPage';
import { PayoutsConsolePage } from './pages/field-partners/PayoutsConsolePage';
import { FraudReviewPage } from './pages/field-partners/FraudReviewPage';
import { FieldProgramConfigPage } from './pages/field-partners/FieldProgramConfigPage';
// Inventory Expansion (Apr 2026)
import { VendorReturnsPage } from './pages/procurement/VendorReturnsPage';
import { VendorReturnDetailPage } from './pages/procurement/VendorReturnDetailPage';
import { PurchaseRequestsPage } from './pages/procurement/PurchaseRequestsPage';
import { PurchaseRequestDetailPage } from './pages/procurement/PurchaseRequestDetailPage';
// Portals (standalone, no AppShell)
import { ClientPortalPage } from './pages/portals/ClientPortalPage';
import { VendorPortalPage } from './pages/portals/VendorPortalPage';
import { PaymentConfirmationForm } from './pages/portals/PaymentConfirmationForm';
// Sales Manager dual-tab pages (Apr 22, 2026 exec sprint — replaces /tasks + /team-performance for sales role)
import { ActivitiesDualPage } from './pages/sales/ActivitiesDualPage';
import { RepsDualPage } from './pages/sales/RepsDualPage';
// 404 fallback (Apr 23, 2026 — Agent 4 fix for silent blank routes)
import { NotFound } from './pages/NotFound';
// Lead Manager (Apr 28, 2026 — Squad-D, 12 screens). The 12th screen
// (admin approvals) reuses the existing AdminLeadApprovalsPage import that
// Squad-B already wired further up — Squad-D just upgraded the body of that
// component with the richer card-based queue from the design canvas.
import { LeadManagerHome } from './pages/lead-manager/LeadManagerHome';
import { UnverifiedQueuePage as LMUnverifiedQueuePage } from './pages/lead-manager/UnverifiedQueuePage';
import { VerifyLeadPage } from './pages/lead-manager/VerifyLeadPage';
import { VerifiedPoolPage as LMVerifiedPoolPage } from './pages/lead-manager/VerifiedPoolPage';
import { AssignedOversightPage } from './pages/lead-manager/AssignedOversightPage';
import { ReassignmentOutboxPage } from './pages/lead-manager/ReassignmentOutboxPage';
import { LeadMetricsPage } from './pages/lead-manager/LeadMetricsPage';
import { LeadSearchPage } from './pages/lead-manager/LeadSearchPage';
import { LeadDetailPage } from './pages/lead-manager/LeadDetailPage';
import { LeadManagerSettingsPage } from './pages/lead-manager/LeadManagerSettingsPage';

// ─── Lazy-loaded spinner ───
function LazySpinner() {
  return (
    <div className="flex h-full items-center justify-center py-32">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
    </div>
  );
}

// ─── Lazy: Accounting (heavy — Tally-grade ledger with 12 tabs) ───
const AccountingPage = lazy(() => import('./pages/accounting/AccountingPage').then(m => ({ default: m.AccountingPage })));
const LedgerAccountingPage = lazy(() => import('./pages/accounting/LedgerAccountingPage').then(m => ({ default: m.LedgerAccountingPage })));
const PaymentConfirmationPage = lazy(() => import('./pages/accounting/PaymentConfirmationPage').then(m => ({ default: m.PaymentConfirmationPage })));

/* ── Sprint 2-3 — Accounting spine (May 24 2026) ───────────────────────
 * Agent KK — Purchase Invoices (vendor bills + editor)
 * Agent LL — Ledger pack (5 reports + voucher register)
 * Agent MM — Coupon editor
 * Agent JJ — PaymentSchedulePanel mounted inline in InvoiceEditorPage.
 */
const PurchaseInvoicesPage = lazy(() => import('./pages/PurchaseInvoices/PurchaseInvoicesPage').then(m => ({ default: m.PurchaseInvoicesPage })));
const PurchaseInvoiceEditor = lazy(() => import('./pages/PurchaseInvoices/PurchaseInvoiceEditor').then(m => ({ default: m.PurchaseInvoiceEditor })));
const VoucherRegister = lazy(() => import('./pages/Ledger/VoucherRegister'));
const AccountLedger = lazy(() => import('./pages/Ledger/AccountLedger'));
const TrialBalance = lazy(() => import('./pages/Ledger/TrialBalance'));
const ProfitLoss = lazy(() => import('./pages/Ledger/ProfitLoss'));
const BalanceSheet = lazy(() => import('./pages/Ledger/BalanceSheet'));
// [2026-07-20 payroll] Deep payroll suite (shared, role-gated backend)
const PayrollPage = lazy(() => import('./pages/payroll/PayrollPage').then(m => ({ default: m.PayrollPage })));
// Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON) — wires the formerly
// dark /api/v1/accounting/* backend (payments doctypes, reconciliation, period
// close, server-computed reports).
const ReportsHubPage = lazy(() => import('./pages/accounting/suite/reports/ReportsHubPage').then(m => ({ default: m.ReportsHubPage })));
const PaymentsHubPage = lazy(() => import('./pages/accounting/suite/payments/PaymentsHubPage').then(m => ({ default: m.PaymentsHubPage })));
const PeriodCloseHubPage = lazy(() => import('./pages/accounting/suite/period/PeriodCloseHubPage').then(m => ({ default: m.PeriodCloseHubPage })));
// === GSTPACK-TLY ===
const TallyExportPage = lazy(() => import('./pages/integrations/TallyExportPage').then(m => ({ default: m.TallyExportPage })));
// === GSTPACK-G2B === (2026-07-19) — GSTR-2A/2B reconciliation
const Gstr2bReconciliationPage = lazy(() => import('./pages/finance/Gstr2bReconciliationPage').then(m => ({ default: m.Gstr2bReconciliationPage })));
// === GSTPACK-GS9 === GSTR-9 annual return (2026-07-19)
const Gstr9Page = lazy(() => import('./pages/finance/Gstr9Page').then(m => ({ default: m.Gstr9Page })));
const CouponEditor = lazy(() => import('./pages/Coupons/CouponEditor').then(m => ({ default: m.CouponEditor })));

// ─── Lazy: Analytics (Recharts-heavy) ───
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));

// ─── Lazy: Manufacturing (20 pages) ───
const BOMEditorPage = lazy(() => import('./pages/manufacturing/BOMEditorPage').then(m => ({ default: m.BOMEditorPage })));
const BOMsPage = lazy(() => import('./pages/manufacturing/BOMsPage').then(m => ({ default: m.BOMsPage })));
const ProductionOrderCreatePage = lazy(() => import('./pages/manufacturing/ProductionOrderCreatePage').then(m => ({ default: m.ProductionOrderCreatePage })));
const ProductionOrderDetailPage = lazy(() => import('./pages/manufacturing/ProductionOrderDetailPage').then(m => ({ default: m.ProductionOrderDetailPage })));
const QCTemplatesPage = lazy(() => import('./pages/manufacturing/QCTemplatesPage').then(m => ({ default: m.QCTemplatesPage })));
const RejectionsPage = lazy(() => import('./pages/manufacturing/RejectionsPage').then(m => ({ default: m.RejectionsPage })));
// Apr 24, 2026 — v3 Production Dashboard is the canonical Production view.
// The legacy standalone ManufacturingDashboardPage (at /manufacturing/shop-floor)
// has been removed; its full kanban is now embedded at the bottom of the v3
// dashboard via <PipelineKanbanBoard />.
const ProductionDashboardV3 = lazy(() => import('./pages/dashboard/roles/ProductionDashboard').then(m => ({ default: m.ProductionDashboard })));
const ProductionPlanningPage = lazy(() => import('./pages/manufacturing/ProductionPlanningPage').then(m => ({ default: m.ProductionPlanningPage })));
const BucketedProductionPlanPage = lazy(() => import('./pages/manufacturing/BucketedProductionPlanPage').then(m => ({ default: m.BucketedProductionPlanPage })));
const ShiftControlPage = lazy(() => import('./pages/manufacturing/ShiftControlPage').then(m => ({ default: m.ShiftControlPage })));
const RMInspectionPage = lazy(() => import('./pages/manufacturing/RMInspectionPage').then(m => ({ default: m.RMInspectionPage })));
const ProcessKanbanPage = lazy(() => import('./pages/manufacturing/ProcessKanbanPage').then(m => ({ default: m.ProcessKanbanPage })));
const QCManagementPage = lazy(() => import('./pages/manufacturing/QCManagementPage').then(m => ({ default: m.QCManagementPage })));
const ManufacturingDispatchPage = lazy(() => import('./pages/manufacturing/ManufacturingDispatchPage').then(m => ({ default: m.ManufacturingDispatchPage })));
const ManufacturingAlertsPage = lazy(() => import('./pages/manufacturing/ManufacturingAlertsPage'));
const ProductionPipelinePage = lazy(() => import('./pages/manufacturing/ProductionPipelinePage').then(m => ({ default: m.ProductionPipelinePage })));
const EndOfShiftReportPage = lazy(() => import('./pages/manufacturing/EndOfShiftReportPage').then(m => ({ default: m.EndOfShiftReportPage })));
const BarcodeManagementPage = lazy(() => import('./pages/manufacturing/BarcodeManagementPage').then(m => ({ default: m.BarcodeManagementPage })));
const ScrapManagementPage = lazy(() => import('./pages/manufacturing/ScrapManagementPage').then(m => ({ default: m.ScrapManagementPage })));
// PipelineConfigPage merged into ProcessKanbanPage as a drawer — route redirects to process-kanban
const FGActivationPage = lazy(() => import('./pages/manufacturing/FGActivationPage').then(m => ({ default: m.FGActivationPage })));
const FloorInspectionPage = lazy(() => import('./pages/manufacturing/FloorInspectionPage').then(m => ({ default: m.FloorInspectionPage })));
const SsTankPackingPage = lazy(() => import('./pages/manufacturing/packing/SsTankPackingPage').then(m => ({ default: m.SsTankPackingPage })));
const SolarWhPackingPage = lazy(() => import('./pages/manufacturing/packing/SolarWhPackingPage').then(m => ({ default: m.SolarWhPackingPage })));
const HeatPumpPackingPage = lazy(() => import('./pages/manufacturing/packing/HeatPumpPackingPage').then(m => ({ default: m.HeatPumpPackingPage })));

// ─── Lazy: Warehouse (20 pages) ───
const InventoryDashboardPage = lazy(() => import('./pages/warehouse/InventoryDashboardPage').then(m => ({ default: m.InventoryDashboardPage })));
const InwardDashboardPage = lazy(() => import('./pages/warehouse/InwardDashboardPage').then(m => ({ default: m.InwardDashboardPage })));
const DispatchDashboardPage = lazy(() => import('./pages/warehouse/DispatchDashboardPage').then(m => ({ default: m.DispatchDashboardPage })));
const ProductsPage = lazy(() => import('./pages/warehouse/ProductsPage').then(m => ({ default: m.ProductsPage })));
const ProductCataloguePage = lazy(() => import('./pages/warehouse/ProductCataloguePage').then(m => ({ default: m.ProductCataloguePage })));
const ProductSettingsPage = lazy(() => import('./pages/warehouse/ProductSettingsPage').then(m => ({ default: m.ProductSettingsPage })));
const InventoryPage = lazy(() => import('./pages/warehouse/InventoryPage').then(m => ({ default: m.InventoryPage })));
const InventoryPipelinePage = lazy(() => import('./pages/warehouse/InventoryPipelinePage').then(m => ({ default: m.InventoryPipelinePage })));
const ProductionPage = lazy(() => import('./pages/warehouse/ProductionPage').then(m => ({ default: m.ProductionPage })));
const StockAuditPage = lazy(() => import('./pages/warehouse/StockAuditPage').then(m => ({ default: m.StockAuditPage })));
const BatchBarcodePrintPage = lazy(() => import('./pages/warehouse/BatchBarcodePrintPage').then(m => ({ default: m.BatchBarcodePrintPage })));
const WarehouseLocationsPage = lazy(() => import('./pages/warehouse/WarehouseLocationsPage').then(m => ({ default: m.WarehouseLocationsPage })));
const MaterialBoxesPage = lazy(() => import('./pages/warehouse/MaterialBoxesPage').then(m => ({ default: m.MaterialBoxesPage })));
const ProductUnitsPage = lazy(() => import('./pages/warehouse/ProductUnitsPage'));
const BarcodeLifecyclePage = lazy(() => import('./pages/warehouse/BarcodeLifecyclePage'));
const DispatchSlipsPage = lazy(() => import('./pages/warehouse/DispatchSlipsPage').then(m => ({ default: m.DispatchSlipsPage })));
const DailyDispatchRegisterPage = lazy(() => import('./pages/warehouse/DailyDispatchRegisterPage').then(m => ({ default: m.DailyDispatchRegisterPage })));
const DailyReceivingRegisterPage = lazy(() => import('./pages/warehouse/DailyReceivingRegisterPage').then(m => ({ default: m.DailyReceivingRegisterPage })));
const BarcodeOnboardingPage = lazy(() => import('./pages/warehouse/BarcodeOnboardingPage').then(m => ({ default: m.BarcodeOnboardingPage })));
const CustomerReturnsPage = lazy(() => import('./pages/warehouse/CustomerReturnsPage').then(m => ({ default: m.CustomerReturnsPage })));
const ReturnDashboardPage = lazy(() => import('./pages/warehouse/ReturnDashboardPage').then(m => ({ default: m.ReturnDashboardPage })));
const CashRewardStickersPage = lazy(() => import('./pages/warehouse/CashRewardStickersPage').then(m => ({ default: m.CashRewardStickersPage })));

// ─── Lazy: GST Compliance (3 pages) ───
const EInvoiceDashboard = lazy(() => import('./pages/finance/EInvoiceDashboard').then(m => ({ default: m.EInvoiceDashboard })));
const EWayBillDashboard = lazy(() => import('./pages/finance/EWayBillDashboard').then(m => ({ default: m.EWayBillDashboard })));
const GSTFilingPage = lazy(() => import('./pages/finance/GSTFilingPage').then(m => ({ default: m.GSTFilingPage })));

// ─── Lazy: Logistics (12 pages) ───
const LogisticsRateComparisonPage = lazy(() => import('./pages/logistics/LogisticsRateComparisonPage').then(m => ({ default: m.LogisticsRateComparisonPage })));
// VedaAgroTruckTrackingPage import removed P2 — /veda-agro/truck-tracking now redirects to /logistics/tracker
const TransportPartnersPage = lazy(() => import('./pages/logistics/TransportPartnersPage').then(m => ({ default: m.TransportPartnersPage })));
// ─── Lazy: Veda Agro Phase 2 — Trade Routes ───
const TradeRoutesPage = lazy(() => import('./pages/veda-agro/trade-routes/TradeRoutesPage').then(m => ({ default: m.TradeRoutesPage })));
// ─── Lazy: Veda Agro v3 commodity deal flow (6 pages) ───
const VedaLeadInbox = lazy(() => import('./pages/veda-agro/VedaLeadInbox').then(m => ({ default: m.VedaLeadInbox })));
const RejectionSettlementsPage = lazy(() => import('./pages/veda-agro/lot-expense/RejectionSettlementsPage').then(m => ({ default: m.RejectionSettlementsPage })));
const RejectionSettlementDetailPage = lazy(() => import('./pages/veda-agro/lot-expense/RejectionSettlementDetailPage').then(m => ({ default: m.RejectionSettlementDetailPage })));
const PaymentAdvicePage = lazy(() => import('./pages/veda-agro/finance/PaymentAdvicePage').then(m => ({ default: m.PaymentAdvicePage }))); // AI-4d2e
const TurnPaperPage = lazy(() => import('./pages/veda-agro/execution/TurnPaperPage').then(m => ({ default: m.TurnPaperPage }))); // AI-xorm
const VedaPurchaseCockpit = lazy(() => import('./pages/veda-agro/VedaPurchaseCockpit').then(m => ({ default: m.VedaPurchaseCockpit })));
const VedaAgroEnquiriesPageV3 = lazy(() => import('./pages/veda-agro/VedaAgroEnquiriesPageV3').then(m => ({ default: m.VedaAgroEnquiriesPageV3 })));
const VedaSourcingWorkbench = lazy(() => import('./pages/veda-agro/VedaSourcingWorkbench').then(m => ({ default: m.VedaSourcingWorkbench })));
const VedaDealDeskRedesign = lazy(() => import('./pages/veda-agro/deal-desk-v2/VedaDealDeskRedesign').then(m => ({ default: m.VedaDealDeskRedesign })));
const VedaApprovalsPage = lazy(() => import('./pages/veda-agro/VedaApprovalsPage').then(m => ({ default: m.VedaApprovalsPage })));
const VedaRateUpdatePage = lazy(() => import('./pages/veda-agro/VedaRateUpdatePage').then(m => ({ default: m.VedaRateUpdatePage })));
const VedaDirectoryPage = lazy(() => import('./pages/veda-agro/VedaDirectoryPage').then(m => ({ default: m.VedaDirectoryPage })));
const VedaMappingManagerPage = lazy(() => import('./pages/veda-agro/VedaMappingManagerPage').then(m => ({ default: m.VedaMappingManagerPage })));
const VedaCustomersPage = lazy(() => import('./pages/veda-agro/customers/VedaCustomersPage').then(m => ({ default: m.VedaCustomersPage })));
const VedaSalesManagerDashboard = lazy(() => import('./pages/veda-agro/cockpit/VedaSalesManagerDashboard').then(m => ({ default: m.VedaSalesManagerDashboard })));
const VedaLogisticsCockpit = lazy(() => import('./pages/veda-agro/cockpit/VedaLogisticsCockpit').then(m => ({ default: m.VedaLogisticsCockpit })));
// Admin Dashboard (2026-09-26) — admin/owner landing: approve, pipeline, team, stuck.
const VedaAdminDashboard = lazy(() => import('./pages/veda-agro/cockpit/VedaAdminDashboard').then(m => ({ default: m.VedaAdminDashboard })));
// Accounts Cockpit (2026-09-25) — Sale Bill / Purchase Bill / Final Settlement queues for the accountant.
const VedaAccountsCockpit = lazy(() => import('./pages/veda-agro/accounts/VedaAccountsCockpit').then(m => ({ default: m.VedaAccountsCockpit })));
// Accounts truck form (2026-09-25) — one page per truck: sale bill, purchase bill, settlement.
const VedaAccountsTruckForm = lazy(() => import('./pages/veda-agro/accounts/VedaAccountsTruckForm').then(m => ({ default: m.VedaAccountsTruckFormRoute })));
const VedaPipelineDashboard = lazy(() => import('./pages/veda-agro/VedaPipelineDashboard').then(m => ({ default: m.VedaPipelineDashboard })));
const VedaSystemHealthDashboard = lazy(() => import('./pages/veda-agro/VedaSystemHealthDashboard').then(m => ({ default: m.VedaSystemHealthDashboard })));
// Rep-safe lead capture page (no sourcing) — routes /veda-agro/leads/:id + /veda-agro/leads/new
const VedaLeadCapturePage = lazy(() => import('./pages/veda-agro/cockpit/VedaLeadCapturePage').then(m => ({ default: m.VedaLeadCapturePage })));
// Call Queue — rep's primary call-desk tool (no sourcing/margin)
const VedaCallQueuePage = lazy(() => import('./pages/veda-agro/cockpit/VedaCallQueuePage').then(m => ({ default: m.VedaCallQueuePage })));
// ─── Lazy: Veda Agro Track 2 — Purchase inbox/profitability + rep renegotiate ───
const VedaEnquiryInbox = lazy(() => import('./pages/veda-agro/track2/VedaEnquiryInbox').then(m => ({ default: m.VedaEnquiryInbox })));
const VedaEnquiryProfitability = lazy(() => import('./pages/veda-agro/track2/VedaEnquiryProfitability').then(m => ({ default: m.VedaEnquiryProfitability })));
const VedaRenegotiateResponse = lazy(() => import('./pages/veda-agro/track2/VedaRenegotiateResponse').then(m => ({ default: m.VedaRenegotiateResponse })));
const VedaRenegotiationsDashboard = lazy(() => import('./pages/veda-agro/VedaRenegotiationsDashboard').then(m => ({ default: m.VedaRenegotiationsDashboard }))); // V6: send-back dashboard (plural, ≠ /renegotiate)
const VedaRenegRespondPage = lazy(() => import('./pages/veda-agro/VedaRenegRespondPage').then(m => ({ default: m.VedaRenegRespondPage }))); // V10: standalone reneg response (sales, no Deal Desk)
const VedaFreightDeskPage = lazy(() => import('./pages/veda-agro/track2/VedaFreightDeskPage').then(m => ({ default: m.VedaFreightDeskPage })));
const FreightQueuePage = lazy(() => import('./pages/veda-agro/track2/FreightQueuePage').then(m => ({ default: m.FreightQueuePage })));
// ─── Lazy: Veda Agro Phase 5 — Stage Tracker (flag-aware, dark at deploy) ───
const StageTrackerPage = lazy(() => import('./pages/veda-agro/stage-tracker/StageTrackerPage').then(m => ({ default: m.StageTrackerPage })));
const VedaMyPipelinesPage = lazy(() => import('./pages/veda-agro/my-pipelines/VedaMyPipelinesPage').then(m => ({ default: m.VedaMyPipelinesPage })));
// Delivery Pipeline (2026-09-13) — every truck still MOVING, with its money.
// 🔒 A SEPARATE PAGE FROM MY PIPELINES BY DESIGN (SPEC-delivery-pipeline R2).
// Manak: "we dont use same code because we will show more info copy code for a
// new page". The two boards answer different questions for different people.
//
// 🔒 BOUND AS `Veda…` BECAUSE `DeliveryPipelinePage` IS ALREADY TAKEN, by the
// Sun Stellar logistics board at ./pages/logistics/DeliveryPipelinePage, routed
// at /logistics/pipeline. Two `const`s of one name in this file is a hard tsc
// error (TS2451) and both routes silently resolve to whichever won — the same
// reason `VedaMyPipelinesPage` carries the prefix three lines above. The
// backend pair does NOT collide: /api/v1/veda-agro/... against
// /api/v1/logistics/..., two different mounts.
const VedaDeliveryPipelinePage = lazy(() => import('./pages/veda-agro/delivery-pipeline/DeliveryPipelinePage').then(m => ({ default: m.DeliveryPipelinePage })));
const VedaTruckDossierPage = lazy(() => import('./pages/veda-agro/delivery-pipeline/TruckDossierPage').then(m => ({ default: m.TruckDossierPage })));
// Credit Ledger (2026-09-23) — every open credit obligation, plant or transporter.
const VedaCreditLedgerPage = lazy(() => import('./pages/veda-agro/credit-ledger/CreditLedgerPage').then(m => ({ default: m.CreditLedgerPage })));
// Closing Register + Deal Dossier — where a deal goes after Final Settlement is advanced.
const ClosingRegisterPage = lazy(() => import('./pages/veda-agro/closing/ClosingRegisterPage').then(m => ({ default: m.ClosingRegisterPage })));
const DealDossierPage = lazy(() => import('./pages/veda-agro/closing/DealDossierPage').then(m => ({ default: m.DealDossierPage })));
const RejectedDispatchesPage = lazy(() => import('./pages/veda-agro/rejected/RejectedDispatchesPage'));
const HoldingDeskPage = lazy(() => import('./pages/veda-agro/holding/HoldingDeskPage'));
const LotHome = lazy(() => import('./pages/veda-agro/holding/LotHome'));
const GodownsPage = lazy(() => import('./pages/veda-agro/masters/GodownsPage'));
// ─── Lazy: Admin — Stage Master Editor (flag-aware, dark at deploy) ───
const StageMasterEditorPage = lazy(() => import('./pages/admin/StageMasterEditorPage').then(m => ({ default: m.StageMasterEditorPage })));
const CompanyProfilePage = lazy(() => import('./pages/admin/CompanyProfilePage').then(m => ({ default: m.CompanyProfilePage })));
// ─── Lazy: Veda Agro REV3 T3 masters + T5 orders + T6 work plan + T8 HRMS/tickets ───
const VedaVendorListPage = lazy(() => import('./pages/veda-agro/VedaVendorWizardPage').then(m => ({ default: m.VedaVendorListPage })));
const VedaVendorWizardPage = lazy(() => import('./pages/veda-agro/VedaVendorWizardPage').then(m => ({ default: m.VedaVendorWizardPage })));
const VedaTransporterListPage = lazy(() => import('./pages/veda-agro/VedaTransporterFormPage').then(m => ({ default: m.VedaTransporterListPage })));
const VedaTransporterFormPage = lazy(() => import('./pages/veda-agro/VedaTransporterFormPage').then(m => ({ default: m.VedaTransporterFormPage })));
// ─── Lazy: Veda Agro TMS (fleet, dispatch, on-route) ───
const VedaDispatchPrepPage = lazy(() => import('./pages/veda-agro/tms/VedaDispatchPrepPage').then(m => ({ default: m.VedaDispatchPrepPage })));
const VedaOnRoutePage = lazy(() => import('./pages/veda-agro/tms/VedaOnRoutePage').then(m => ({ default: m.VedaOnRoutePage })));
const TripLocatePublicPage = lazy(() => import('./pages/public/TripLocatePublicPage').then(m => ({ default: m.TripLocatePublicPage })));
const VedaProductMasterPage = lazy(() => import('./pages/veda-agro/VedaProductMasterPage').then(m => ({ default: m.VedaProductMasterPage })));
const VedaOrdersListPage = lazy(() => import('./pages/veda-agro/VedaOrdersListPage').then(m => ({ default: m.VedaOrdersListPage })));
const VedaSalesOrderFormPage = lazy(() => import('./pages/veda-agro/VedaSalesOrderFormPage').then(m => ({ default: m.VedaSalesOrderFormPage })));
const VedaPurchaseOrderFormPage = lazy(() => import('./pages/veda-agro/VedaPurchaseOrderFormPage').then(m => ({ default: m.VedaPurchaseOrderFormPage })));
const VedaWorkPlanPage = lazy(() => import('./pages/veda-agro/VedaWorkPlanPage').then(m => ({ default: m.VedaWorkPlanPage })));
const VedaLeavePage = lazy(() => import('./pages/veda-agro/VedaLeavePage').then(m => ({ default: m.VedaLeavePage })));
const VedaAttendancePage = lazy(() => import('./pages/veda-agro/VedaAttendancePage').then(m => ({ default: m.VedaAttendancePage })));
const VedaResolutionPage = lazy(() => import('./pages/veda-agro/VedaResolutionPage').then(m => ({ default: m.VedaResolutionPage })));
const ShipmentsPage = lazy(() => import('./pages/logistics/ShipmentsPage').then(m => ({ default: m.ShipmentsPage })));
const ShipmentDetailPage = lazy(() => import('./pages/logistics/ShipmentDetailPage').then(m => ({ default: m.ShipmentDetailPage })));
const LogisticsFlowPage = lazy(() => import('./pages/logistics/LogisticsFlowPage').then(m => ({ default: m.LogisticsFlowPage })));
const DispatchMobileFallbackPage = lazy(() => import('./pages/logistics/DispatchMobileFallbackPage'));
const DispatchConfigPage = lazy(() => import('./pages/logistics/DispatchConfigPage').then(m => ({ default: m.DispatchConfigPage })));
const SODispatchCreatorPage = lazy(() => import('./pages/logistics/SODispatchCreatorPage').then(m => ({ default: m.SODispatchCreatorPage })));
const DispatchSlipDetailPage = lazy(() => import('./pages/logistics/DispatchSlipDetailPage').then(m => ({ default: m.DispatchSlipDetailPage })));
const DeliveryPipelinePage = lazy(() => import('./pages/logistics/DeliveryPipelinePage').then(m => ({ default: m.DeliveryPipelinePage })));
const PostDispatchTrackerPage = lazy(() => import('./pages/logistics/PostDispatchTrackerPage').then(m => ({ default: m.PostDispatchTrackerPage })));
const WaTemplateHealthPage = lazy(() => import('./pages/logistics/WaTemplateHealthPage').then(m => ({ default: m.WaTemplateHealthPage })));
const PaymentGatePage = lazy(() => import('./pages/logistics/PaymentGatePage').then(m => ({ default: m.PaymentGatePage })));

// ─── Lazy: Executive sub-pages (14 — Apr 22, 2026 exec sprint) ───
// Sales exec
const ExecCallQueuePage = lazy(() => import('./pages/executive/sales/CallQueuePage').then(m => ({ default: m.CallQueuePage })));
const ExecMyDealsPage = lazy(() => import('./pages/executive/sales/MyDealsPage').then(m => ({ default: m.MyDealsPage })));
const ExecIncentivesPage = lazy(() => import('./pages/executive/sales/IncentivesPage').then(m => ({ default: m.IncentivesPage })));
const ExecCallHistoryPage = lazy(() => import('./pages/executive/sales/CallHistoryPage').then(m => ({ default: m.CallHistoryPage })));
// Service tech
const ExecMyTicketsPage = lazy(() => import('./pages/executive/service/MyTicketsPage').then(m => ({ default: m.MyTicketsPage })));
const ExecPartsAndExpensesPage = lazy(() => import('./pages/executive/service/PartsAndExpensesPage').then(m => ({ default: m.PartsAndExpensesPage })));
// Production operator
const ExecMyWorkOrdersPage = lazy(() => import('./pages/executive/production/MyWorkOrdersPage').then(m => ({ default: m.MyWorkOrdersPage })));
const ExecMyQCCheckpointsPage = lazy(() => import('./pages/executive/production/MyQCCheckpointsPage').then(m => ({ default: m.MyQCCheckpointsPage })));
// Warehouse exec
const ExecGRNQueuePage = lazy(() => import('./pages/executive/warehouse/GRNQueuePage').then(m => ({ default: m.GRNQueuePage })));
const ExecMyDispatchPage = lazy(() => import('./pages/executive/warehouse/MyDispatchPage').then(m => ({ default: m.MyDispatchPage })));
// Accounts exec
const ExecMatchQueuePage = lazy(() => import('./pages/executive/accounts/MatchQueuePage').then(m => ({ default: m.MatchQueuePage })));
const ExecApprovalsPage = lazy(() => import('./pages/executive/accounts/ApprovalsPage').then(m => ({ default: m.ApprovalsPage })));
// Field partner
const ExecMyClaimsPage = lazy(() => import('./pages/executive/field-partner/MyClaimsPage').then(m => ({ default: m.MyClaimsPage })));
const ExecMyPayoutsPage = lazy(() => import('./pages/executive/field-partner/MyPayoutsPage').then(m => ({ default: m.MyPayoutsPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// [2026-07-19 plan1-common] RequireRoles — client route-gate for the Deep
// Accounting Suite. NOT a security boundary; the backend role-gates the routes.
function RequireRoles({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return null; // store still hydrating — not-yet-authorized, no fetch
  const roleName = (user.role?.name ?? '').toLowerCase();
  if (!roles.includes(roleName)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Strict-view route guard — redirects lead_triage_coordinator away from routes
// they have no business reaching (Settings, etc). Server already blocks the
// underlying writes; this just keeps the UI honest.
function StrictViewBlock({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role?.name === 'lead_triage_coordinator') {
    return <Navigate to="/leads/triage" replace />;
  }
  return <>{children}</>;
}

// VedaSoCreateGuard — manual SO creation is a purchase/admin tool (2026-07-07 approval
// guardrails F2). Reps (sales_manager / sales_rep) raise Enquiries instead, so redirect
// them off /veda-agro/so/new to the enquiries surface. Viewing existing SOs (/so/:id) is
// unguarded — read side stays reachable.
function VedaSoCreateGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const canCreateSO = ['purchase_head', 'owner', 'admin', 'super_admin'].includes(
    (user?.role?.name ?? '').toLowerCase(),
  );
  if (!canCreateSO) return <Navigate to="/veda-agro/enquiries" replace />;
  return <>{children}</>;
}

/**
 * DealDeskGuard — renders the Sourcing Workbench for admin/owner/purchase/purchase_head roles.
 * Sales roles (sales_rep, sales_manager) are the purchase-side Deal Desk's OFF-LIMITS
 * audience: the Deal Desk exposes sourcing / plants / vendors / margins / Submit-to-MD.
 * V10 (2026-07-17): a sales hit on /veda-agro/enquiries/:id is redirected to the
 * standalone reneg-response page (/veda-agro/renegotiations/:enquiryId → RenegRespondPanel
 * only). This closes the by-URL sourcing leak (the V6 "Open →" dumped reps here).
 * purchase_head passes through (same as admin/owner) — no extra check needed.
 */
const SALES_ROLES = new Set(['sales_rep', 'sales_manager']);
function DealDeskGuard() {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  // Sales roles never see the sourcing Deal Desk — route them to their standalone
  // reneg-response page for this enquiry (empty state there if no open cycle).
  if (SALES_ROLES.has(user?.role?.name ?? '')) {
    return <Navigate to={`/veda-agro/renegotiations/${id}`} replace />;
  }
  // New map-less Deal Desk is the default render; the legacy three-panel
  // Sourcing Workbench stays reachable at ?view=legacy.
  if (searchParams.get('view') === 'legacy') return <VedaSourcingWorkbench />;
  return <VedaDealDeskRedesign />;
}

/**
 * Bug B-2 (May 2026, QA-FIX): standalone /logout route. Previously the only
 * way to log out was via the avatar dropdown in TopBar; navigating to
 * `/logout` returned a 404. Calls the auth store's logout() (which clears
 * the cookie + JWT server-side and resets local state) then redirects
 * to /login. The dropdown handler in TopBar.tsx still works alongside this.
 */
function LogoutPage() {
  const { logout } = useAuthStore();
  const [done, setDone] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await logout();
      } catch {
        // logout() already swallows errors; we still want to redirect.
      } finally {
        if (!cancelled) setDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logout]);

  if (done) return <Navigate to="/login" replace />;
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
    </div>
  );
}

/**
 * Apr 23, 2026 (Bug 1) — reset the ErrorBoundary whenever the URL changes so a
 * crash on one page doesn't stick the user on a fallback after navigation. The
 * keyed wrapper works because React unmounts / remounts children on key change.
 */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} label={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* AI-vbof — public self-service password reset. /reset-password is the
          page the backend's reset email links to (${FRONTEND_URL}/reset-password?token=…);
          it 404'd before this route existed. /forgot-password is linked from
          LoginPage. Both are unauthenticated (no ProtectedRoute wrapper). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Bug B-2 (May 2026, QA-FIX): standalone /logout route — clears
          session then redirects to /login. Avatar dropdown in TopBar
          still works in parallel. */}
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/portal/client" element={<ClientPortalPage />} />
      <Route path="/portal/vendor" element={<VendorPortalPage />} />
      {/* Public payment confirmation form — customer submits UTR + screenshot */}
      <Route path="/payment-confirmation/:id" element={<PaymentConfirmationForm />} />
      {/* Public TMS: driver shares GPS location — no auth required */}
      <Route path="/trip-locate/:token" element={<TripLocatePublicPage />} />

      {/* Protected app routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <RoutedErrorBoundary>
              <Suspense fallback={<LazySpinner />}>
              <Routes>
                <Route path="/" element={<RoleAwareHome />} />
                <Route path="/dashboard" element={<RoleAwareDashboard />} />
                {/* v6.8.x: /reports was 404 because sidebar's Reports is a group header with no route.
                    Redirect to Accounting → Profit & Loss as the canonical reports surface. */}
                <Route path="/reports" element={<Navigate to="/accounting?tab=pl" replace />} />
                {/* Customers */}
                <Route path="/customers" element={<CustomersPage />} />
                {/* CRM */}
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/:id" element={<ContactDetailPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/accounts/:id" element={<AccountDetailPage />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/deals/:id" element={<DealDetailPage />} />
                {/* Sales */}
                <Route path="/proposals" element={<ProposalsPage />} />
                <Route path="/proposals/new" element={<ProposalEditorPage />} />
                <Route path="/proposals/:id/edit" element={<ProposalEditorPage />} />
                <Route path="/discount-requests" element={<DiscountRequestsPage />} />
                {/* Quotes merged into Proposals — redirect estimates URLs */}
                <Route path="/estimates" element={<Navigate to="/proposals" replace />} />
                <Route path="/estimates/new" element={<Navigate to="/proposals/new" replace />} />
                <Route path="/estimates/:id/edit" element={<Navigate to="/proposals" replace />} />
                {/* Sales Orders */}
                <Route path="/sales-orders" element={<SalesOrdersPage />} />
                <Route path="/sales-orders/new" element={<SalesOrderEditorPage />} />
                <Route path="/sales-orders/:id" element={<SalesOrderDetailPage />} />
                <Route path="/sales-orders/:id/edit" element={<SalesOrderEditorPage />} />
                <Route path="/sales-orders/:id/fulfil" element={<SalesOrderFulfilmentPage />} />
                {/* Finance */}
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/invoices/new" element={<InvoiceEditorPage />} />
                <Route path="/invoices/:id" element={<InvoiceEditorPage />} />
                <Route path="/invoices/:id/edit" element={<InvoiceEditorPage />} />
                <Route path="/payments" element={<PaymentsPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/credit-notes" element={<CreditNotesPage />} />
                <Route path="/veda-agro/legacy-invoices" element={<LegacyInvoicesPage />} />
                <Route path="/veda-agro/payment-advice" element={<PaymentAdvicePage />} />
                <Route path="/veda-agro/turn-paper" element={<TurnPaperPage />} />
                <Route path="/veda-agro/turn-paper/:dealTruckId" element={<TurnPaperPage />} />
                <Route path="/debit-notes" element={<DebitNotesPage />} />
                <Route path="/accounting" element={<LedgerAccountingPage />} />
                {/* [2026-07-20 payroll] role-gated to compensation audience + md */}
                <Route path="/payroll" element={<RequireRoles roles={['owner', 'admin', 'super_admin', 'hr', 'accountant', 'md']}><PayrollPage /></RequireRoles>} />
                {/* [2026-07-19 plan1-common] Deep Accounting Suite — role-gated
                    (admin/owner/super_admin/accountant); Sidebar rows use this list
                    byte-identically. `hr` deliberately excluded — GL/payments are not
                    an HR surface (contrast /payroll). */}
                <Route
                  path="/accounting/suite/reports"
                  element={
                    <RequireRoles roles={['admin', 'owner', 'super_admin', 'accountant']}>
                      <ReportsHubPage />
                    </RequireRoles>
                  }
                />
                <Route
                  path="/accounting/suite/payments"
                  element={
                    <RequireRoles roles={['admin', 'owner', 'super_admin', 'accountant']}>
                      <PaymentsHubPage />
                    </RequireRoles>
                  }
                />
                <Route
                  path="/accounting/suite/period"
                  element={
                    <RequireRoles roles={['admin', 'owner', 'super_admin', 'accountant']}>
                      <PeriodCloseHubPage />
                    </RequireRoles>
                  }
                />
                {/* === GSTPACK-G2B === (2026-07-19) — GSTR-2A/2B reconciliation */}
                <Route path="/gst/gstr2b" element={<Gstr2bReconciliationPage />} />
                {/* === GSTPACK-GS9 === GSTR-9 annual return */}
                <Route path="/gst/gstr9" element={<Gstr9Page />} />
                {/* === GSTPACK-TLY === Tally Prime import-XML export */}
                <Route path="/tally-export" element={<TallyExportPage />} />
                {/* NEW-R2-002 fix (Wave-3 May 1 2026): /accounting/ledger was returning
                    SPA 404 because only /accounting was registered. Add alias so that
                    direct navigation to /accounting/ledger lands on the correct page. */}
                <Route path="/accounting/ledger" element={<LedgerAccountingPage />} />
                <Route path="/accounting/legacy" element={<AccountingPage />} />
                {/* Payment Confirmations — Accounts dashboard (auth-gated) */}
                <Route path="/payment-confirmations" element={<PaymentConfirmationPage />} />
                {/* GST Compliance */}
                <Route path="/gst/e-invoices" element={<EInvoiceDashboard />} />
                <Route path="/gst/e-waybills" element={<EWayBillDashboard />} />
                <Route path="/gst/filing" element={<GSTFilingPage />} />
                {/* BUG-26: AccountsExecDashboard navigates to /finance/gst-filing (and
                    /finance/gst-filing?tab=tds) — alias both to /gst/filing to avoid 404.
                    Tab param is preserved via the navigate(); the GSTFilingPage can extend
                    to read ?tab=tds when TDS support is added. */}
                <Route path="/finance/gst-filing" element={<GSTFilingPage />} />
                {/* ── Sprint 2-3 — accounting spine (May 24 2026) ────────────────
                    Purchase Invoices (KK), Ledger pack (LL), Coupons (MM).
                    PaymentSchedulePanel (JJ) is mounted inline in
                    InvoiceEditorPage — no separate route. */}
                <Route path="/purchase-invoices" element={<PurchaseInvoicesPage />} />
                <Route path="/purchase-invoices/new" element={<PurchaseInvoiceEditor />} />
                <Route path="/purchase-invoices/:id" element={<PurchaseInvoiceEditor />} />
                <Route path="/ledger/vouchers" element={<VoucherRegister />} />
                <Route path="/ledger/vouchers/:id" element={<VoucherRegister />} />
                <Route path="/ledger/accounts/:accountId" element={<AccountLedger />} />
                <Route path="/ledger/trial-balance" element={<TrialBalance />} />
                <Route path="/ledger/profit-loss" element={<ProfitLoss />} />
                <Route path="/ledger/balance-sheet" element={<BalanceSheet />} />
                <Route path="/coupons/new" element={<CouponEditor />} />
                <Route path="/coupons/:id/edit" element={<CouponEditor />} />
                {/* Projects */}
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                {/* Warehouse */}
                <Route path="/warehouse/dashboard" element={<InventoryDashboardPage />} />
                <Route path="/warehouse/inward" element={<InwardDashboardPage />} />
                {/* BUG-C08 fix (Wave-3 May 1 2026): /warehouse/grn was returning SPA 404
                    because only /warehouse/inward was registered. GRN (Goods Receipt Note)
                    creation lives in InwardDashboardPage. Add /warehouse/grn as an alias
                    so sidebar "New GRN" links resolve to the correct page. */}
                <Route path="/warehouse/grn" element={<InwardDashboardPage />} />
                <Route path="/warehouse/dispatch-dashboard" element={<DispatchDashboardPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/product-catalogue" element={<ProductCataloguePage />} />
                <Route path="/product-settings" element={<ProductSettingsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/warehouse/inventory-pipeline" element={<InventoryPipelinePage />} />
                <Route path="/production" element={<ProductionPage />} />
                <Route path="/warehouse/stock-audit" element={<StockAuditPage />} />
                <Route path="/warehouse/barcode-print" element={<BatchBarcodePrintPage />} />
                <Route path="/warehouse/dispatch-register" element={<DailyDispatchRegisterPage />} />
                <Route path="/warehouse/receiving-register" element={<DailyReceivingRegisterPage />} />
                <Route path="/warehouse/barcode-onboarding" element={<BarcodeOnboardingPage />} />
                <Route path="/customer-returns" element={<CustomerReturnsPage />} />
                <Route path="/returns" element={<ReturnDashboardPage />} />
                <Route path="/manufacturing/packing/ss-tank/:id" element={<SsTankPackingPage />} />
                <Route path="/manufacturing/packing/solar-wh/:id" element={<SolarWhPackingPage />} />
                <Route path="/manufacturing/packing/heat-pump/:id" element={<HeatPumpPackingPage />} />
                {/* Warehouse Enhancements */}
                <Route path="/warehouse-locations" element={<WarehouseLocationsPage />} />
                <Route path="/material-boxes" element={<MaterialBoxesPage />} />
                <Route path="/dispatch-slips" element={<DispatchSlipsPage />} />
                {/* Procurement */}
                <Route path="/purchase-dashboard" element={<PurchaseDashboardPage />} />
                <Route path="/vendor-rate-comparison" element={<VendorRateComparisonPage />} />
                <Route path="/vendor-returns" element={<VendorReturnsPage />} />
                <Route path="/vendor-returns/:id" element={<VendorReturnDetailPage />} />
                <Route path="/purchase-requests" element={<PurchaseRequestsPage />} />
                <Route path="/purchase-requests/:id" element={<PurchaseRequestDetailPage />} />
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="/purchase-orders/new" element={<PurchaseOrderEditorPage />} />
                <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderEditorPage />} />
                {/* ── Generic routes (Phase P2 — Apr 22 2026, tenant-hardcoding cleanup) ── */}
                {/* Samples — B2B sampling workflow, generic */}
                <Route path="/samples" element={<SamplesPage />} />
                {/* Landed cost — any importer needs this */}
                <Route path="/procurement/landed-cost" element={<LandedCostPage />} />
                {/* Invoice swap — commercial ↔ customs invoice for exporters */}
                <Route path="/finance/invoice-swap" element={<InvoiceSwapPage />} />
                {/* Financial approvals — multi-level finance approval queue */}
                <Route path="/finance/approvals" element={<FinancialApprovalsListPage />} />
                {/* Security deposits / EMD tracker */}
                <Route path="/finance/security-deposits" element={<SecurityPaymentPage />} />
                {/* Deal closure checklist */}
                <Route path="/deal-closure" element={<DealClosurePage />} />
                {/* Tally sync (per-tenant integration, configurable) */}
                <Route path="/integrations/tally-sync" element={<TallySyncPage />} />

                {/* ── Veda Agro Phase 2 — Trade Routes ── */}
                <Route path="/veda-agro/trade-routes" element={<TradeRoutesPage />} />
                {/* ── Veda Agro v3 commodity deal flow ── */}
                <Route path="/veda-agro/cockpit" element={<VedaPurchaseCockpit />} />
                <Route path="/veda-agro/sales" element={<VedaSalesManagerDashboard />} />
                <Route path="/veda-agro/logistics" element={<VedaLogisticsCockpit />} />
                <Route path="/veda-agro/accounts" element={<VedaAccountsCockpit />} />
                <Route path="/veda-agro/admin" element={<VedaAdminDashboard />} />
                <Route path="/veda-agro/accounts/truck/:dispatchNumber" element={<VedaAccountsTruckForm />} />
                <Route path="/veda-agro/pipeline" element={<VedaPipelineDashboard />} />
                <Route path="/veda-agro/system-health" element={<VedaSystemHealthDashboard />} />
                <Route path="/veda-agro/triage" element={<VedaLeadInbox />} />
                <Route path="/veda-agro/enquiries" element={<VedaAgroEnquiriesPageV3 />} />
                {/* Deal Desk (Sourcing Workbench) — locked for sales_manager.
                    The rep's surfaces are /veda-agro/leads/* instead. */}
                <Route path="/veda-agro/enquiries/:id" element={<DealDeskGuard />} />
                {/* Rep-safe lead capture page — NO sourcing, NO vendors/margins/plants */}
                <Route path="/veda-agro/leads/new" element={<VedaLeadCapturePage />} />
                <Route path="/veda-agro/leads/:id" element={<VedaLeadCapturePage />} />
                {/* Call Queue — rep's primary call-desk (NO sourcing/margin) */}
                <Route path="/veda-agro/call-queue" element={<VedaCallQueuePage />} />
                {/* Phase 5 — Stage Tracker (VEDA_STAGE_MASTER_ENABLED flag; OFF at deploy → calm disabled state) */}
                <Route path="/veda-agro/stage-tracker" element={<StageTrackerPage />} />
                {/* My Pipelines — a rep's own trucks, read-only (2026-09-04). */}
                <Route path="/veda-agro/my-pipelines" element={<VedaMyPipelinesPage />} />
                {/* Delivery Pipeline — every truck still moving, admin-only, read-only.
                    🔒 KEYED ON THE DISPATCH, NEVER THE DEAL. One sales order on .60
                    carries three live trucks at three different weights; a :dealId
                    here would be the deal-vs-truck defect, in the URL bar. */}
                <Route path="/veda-agro/delivery-pipeline" element={<VedaDeliveryPipelinePage />} />
                <Route path="/veda-agro/delivery-pipeline/:dispatchId" element={<VedaTruckDossierPage />} />
                {/* Credit Ledger — every open plant/transporter credit obligation, read-only. */}
                <Route path="/veda-agro/credit-ledger" element={<VedaCreditLedgerPage />} />
                {/* The room on the other side of Final Settlement — read-only. */}
                <Route path="/veda-agro/closing-register" element={<ClosingRegisterPage />} />
                <Route path="/veda-agro/closing-register/:dealId" element={<DealDossierPage />} />
                <Route path="/veda-agro/closing-register/truck/:dispatchId" element={<DealDossierPage />} />
                {/* What fell out of the tracker, and the desk that settles it. */}
                <Route path="/veda-agro/rejected-dispatches" element={<RejectedDispatchesPage />} />
                <Route path="/veda-agro/holding-desk" element={<HoldingDeskPage />} />
                <Route path="/veda-agro/holding/lots/:id" element={<LotHome />} />
                <Route path="/veda-agro/masters/godowns" element={<GodownsPage />} />
                {/* 🔒 The Settlement Desk became the Holding Desk's Approvals
                    lane. The old path stays as a redirect — it is bookmarked,
                    and it is in every "Open on Holding Desk" link already
                    printed on a rejected-dispatch row. */}
                <Route path="/veda-agro/settlement-desk" element={<Navigate to="/veda-agro/holding-desk" replace />} />
                {/* Dedicated two-pane Freight Desk (cockpit shows a compact preview + "See all →") */}
                <Route path="/veda-agro/freight-desk" element={<VedaFreightDeskPage />} />
                <Route path="/veda-agro/freight-queue" element={<FreightQueuePage />} />
                <Route path="/veda-agro/approvals" element={<VedaApprovalsPage />} />
                <Route path="/veda-agro/rejection-settlements" element={<RejectionSettlementsPage />} />
                <Route path="/veda-agro/rejection-settlements/:id" element={<RejectionSettlementDetailPage />} />
                {/* ── Track 2: Purchase enquiry inbox + profitability, Rep renegotiate ── */}
                <Route path="/veda-agro/enquiry-inbox" element={<VedaRenegotiationGuard mode="purchase"><VedaEnquiryInbox /></VedaRenegotiationGuard>} />
                <Route path="/veda-agro/enquiry-inbox/:id" element={<VedaRenegotiationGuard mode="purchase"><VedaEnquiryProfitability /></VedaRenegotiationGuard>} />
                <Route path="/veda-agro/renegotiate" element={<VedaRenegotiationGuard mode="legacy"><VedaRenegotiateResponse /></VedaRenegotiationGuard>} />
                {/* V6: Renegotiation Dashboard (plural) — MD/Purchase send-back queue, role-scoped */}
                <Route path="/veda-agro/renegotiations" element={<VedaRenegotiationsDashboard />} />
                {/* V10: standalone reneg-response page (sales) — ONLY RenegRespondPanel, no Deal Desk */}
                <Route path="/veda-agro/renegotiations/:enquiryId" element={<VedaRenegotiationGuard mode="respond"><VedaRenegRespondPage /></VedaRenegotiationGuard>} />
                <Route path="/veda-agro/rate-update" element={<VedaRateUpdatePage />} />
                <Route path="/veda-agro/directory" element={<VedaDirectoryPage />} />
                {/* Customer Master (2026-07-03) — create for sales/purchase, admin delete/deactivate */}
                <Route path="/veda-agro/customers" element={<VedaCustomersPage />} />
                {/* ── Veda Agro REV3 T3 — masters parity ── */}
                <Route path="/veda-agro/vendors" element={<VedaVendorListPage />} />
                <Route path="/veda-agro/vendors/new" element={<VedaVendorWizardPage />} />
                <Route path="/veda-agro/vendors/:id/edit" element={<VedaVendorWizardPage />} />
                <Route path="/veda-agro/transporters" element={<VedaTransporterListPage />} />
                <Route path="/veda-agro/transporters/new" element={<VedaTransporterFormPage />} />
                <Route path="/veda-agro/transporters/:id/edit" element={<VedaTransporterFormPage />} />
                <Route path="/veda-agro/mapping" element={<VedaMappingManagerPage />} />
                <Route path="/veda-agro/products" element={<VedaProductMasterPage />} />
                {/* ── Veda Agro REV3 T5 — manual SO/PO (§13/§14) ── */}
                <Route path="/veda-agro/orders" element={<VedaOrdersListPage />} />
                <Route path="/veda-agro/so/new" element={<VedaSoCreateGuard><VedaSalesOrderFormPage /></VedaSoCreateGuard>} />
                <Route path="/veda-agro/so/:id" element={<VedaSalesOrderFormPage />} />
                <Route path="/veda-agro/po/new" element={<VedaPurchaseOrderGuard><VedaPurchaseOrderFormPage /></VedaPurchaseOrderGuard>} />
                <Route path="/veda-agro/po/:id" element={<VedaPurchaseOrderGuard><VedaPurchaseOrderFormPage /></VedaPurchaseOrderGuard>} />
                {/* ── Veda Agro REV3 T6 — Work Plan ── */}
                <Route path="/veda-agro/work-plan" element={<VedaWorkPlanPage />} />
                {/* ── Veda Agro REV3 T8 — HRMS + Resolution Centre ── */}
                <Route path="/veda-agro/leaves" element={<VedaLeavePage />} />
                <Route path="/veda-agro/my-attendance" element={<VedaAttendancePage />} />
                <Route path="/veda-agro/resolution-centre" element={<VedaResolutionPage />} />
                {/* ── Veda Agro TMS — dispatch prep + on-route ── */}
                <Route path="/veda-agro/dispatch/:shipmentId" element={<VedaDispatchPrepPage />} />
                <Route path="/veda-agro/on-route" element={<VedaOnRoutePage />} />
                {/* ── Legacy /veda-agro/* aliases (GENERALIZE — keep bookmarks working) ── */}
                <Route path="/veda-agro/samples" element={<SamplesPage />} />
                <Route path="/veda-agro/landed-cost" element={<LandedCostPage />} />
                <Route path="/veda-agro/invoice-swap" element={<InvoiceSwapPage />} />
                <Route path="/veda-agro/financial-approvals" element={<FinancialApprovalsListPage />} />
                <Route path="/veda-agro/security-payments" element={<SecurityPaymentPage />} />
                <Route path="/veda-agro/deal-closure" element={<DealClosurePage />} />
                <Route path="/veda-agro/tally-sync" element={<TallySyncPage />} />

                {/* ── Legacy /veda-agro/* redirects (DEPRECATE — duplicate of existing generics) ── */}
                <Route path="/veda-agro/leads" element={<Navigate to="/leads" replace />} />
                <Route path="/veda-agro/deals" element={<Navigate to="/deals" replace />} />
                <Route path="/veda-agro/po-create" element={<VedaPurchaseOrderGuard><Navigate to="/purchase-orders/new" replace /></VedaPurchaseOrderGuard>} />
                <Route path="/veda-agro/supplier-quotes" element={<Navigate to="/quotation-jobs" replace />} />
                <Route path="/veda-agro/truck-tracking" element={<Navigate to="/logistics/tracker" replace />} />
                <Route path="/veda-agro/payments" element={<Navigate to="/payments" replace />} />
                <Route path="/veda-agro/advances" element={<Navigate to="/payments?tab=advances" replace />} />
                <Route path="/veda-agro/invoice-upload" element={<Navigate to="/expenses?upload=1" replace />} />
                <Route path="/veda-agro/margin-report" element={<Navigate to="/analytics?view=margin" replace />} />
                {/* Quotation Jobs (Procurement Automation) */}
                <Route path="/quotation-jobs" element={<QuotationJobsPage />} />
                <Route path="/quotation-jobs/:id" element={<QuotationJobDetailPage />} />
                {/* Logistics */}
                <Route path="/transport-partners" element={<TransportPartnersPage />} />
                <Route path="/shipments" element={<ShipmentsPage />} />
                <Route path="/dispatch" element={<DispatchMobileFallbackPage />} />
                <Route path="/shipments/:id" element={<ShipmentDetailPage />} />
                <Route path="/logistics/rate-comparison" element={<LogisticsRateComparisonPage />} />
                <Route path="/logistics/flow" element={<LogisticsFlowPage />} />
                <Route path="/payment-gate" element={<PaymentGatePage />} />
                <Route path="/logistics/pipeline" element={<DeliveryPipelinePage />} />
                <Route path="/logistics/tracker" element={<PostDispatchTrackerPage />} />
                <Route path="/logistics/templates" element={<WaTemplateHealthPage />} />
                <Route path="/dispatch-config" element={<DispatchConfigPage />} />
                <Route path="/dispatch-config/from-so/:salesOrderId" element={<SODispatchCreatorPage />} />
                <Route path="/dispatch-config/slip/:id" element={<DispatchSlipDetailPage />} />
                <Route path="/warehouse/cash-rewards" element={<CashRewardStickersPage />} />
                {/* Field Partners */}
                <Route path="/field-partners" element={<FieldPartnerDirectoryPage />} />
                <Route path="/field-partners/kyc-review" element={<KycReviewPage />} />
                <Route path="/field-partners/claims" element={<InstallationClaimsPage />} />
                <Route path="/field-partners/incentive-rules" element={<IncentiveRulesPage />} />
                <Route path="/field-partners/payouts" element={<PayoutsConsolePage />} />
                <Route path="/field-partners/fraud-review" element={<FraudReviewPage />} />
                <Route path="/field-partners/config" element={<FieldProgramConfigPage />} />
                {/* Procurement Contracts */}
                <Route path="/purchase-contracts" element={<PurchaseContractsPage />} />
                <Route path="/transport-contracts" element={<TransportContractsPage />} />
                {/* Manufacturing */}
                <Route path="/manufacturing/boms" element={<BOMsPage />} />
                <Route path="/boms/new" element={<BOMEditorPage />} />
                <Route path="/boms/:id/edit" element={<BOMEditorPage />} />
                <Route path="/manufacturing/orders/new" element={<ProductionOrderCreatePage />} />
                <Route path="/production-orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/manufacturing/orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/manufacturing/production-orders/:id" element={<ProductionOrderDetailPage />} />
                <Route path="/qc-templates" element={<QCTemplatesPage />} />
                <Route path="/rejections" element={<RejectionsPage />} />
                <Route path="/manufacturing/dashboard" element={<ProductionDashboardV3 />} />
                {/* Legacy /manufacturing/shop-floor redirects to the v3 dashboard
                    (kanban is now embedded at the bottom). */}
                <Route path="/manufacturing/shop-floor" element={<Navigate to="/manufacturing/dashboard" replace />} />
                <Route path="/manufacturing/planning" element={<ProductionPlanningPage />} />
                <Route path="/production/plan" element={<BucketedProductionPlanPage />} />
                <Route path="/production-planning" element={<BucketedProductionPlanPage />} />
                <Route path="/manufacturing/shift-control" element={<ShiftControlPage />} />
                <Route path="/manufacturing/rm-inspection" element={<RMInspectionPage />} />
                <Route path="/manufacturing/process-kanban" element={<ProcessKanbanPage />} />
                <Route path="/manufacturing/qc" element={<QCManagementPage />} />
                <Route path="/manufacturing/dispatch" element={<ManufacturingDispatchPage />} />
                <Route path="/manufacturing/alerts" element={<ManufacturingAlertsPage />} />
                <Route path="/manufacturing/pipeline" element={<ProductionPipelinePage />} />
                <Route path="/manufacturing/shift-report" element={<EndOfShiftReportPage />} />
                <Route path="/manufacturing/barcodes" element={<BarcodeManagementPage />} />
                <Route path="/manufacturing/scrap" element={<ScrapManagementPage />} />
                <Route path="/manufacturing/pipeline-config" element={<Navigate to="/manufacturing/process-kanban" replace />} />
                <Route path="/manufacturing/fg-activation" element={<FGActivationPage />} />
                <Route path="/manufacturing/floor-inspection" element={<FloorInspectionPage />} />
                <Route path="/warehouse/barcode-lifecycle" element={<BarcodeLifecyclePage />} />
                <Route path="/warehouse/product-units" element={<ProductUnitsPage />} />
                {/* Service Escalation */}
                <Route path="/escalation-rules" element={<EscalationRulesPage />} />
                {/* Lead Integrations */}
                <Route path="/leads" element={<LeadsPage />} />
                {/* Apr 26, 2026 (Squad-B) — Lead Manager workflow */}
                <Route path="/leads/unverified" element={<UnverifiedLeadsPage />} />
                <Route path="/leads/verified" element={<VerifiedLeadsPage />} />
                <Route path="/leads/approvals" element={<LeadApprovalsPage />} />
                {/* Phase 4 (2026-05-26) — Deepika's lead-triage dashboard */}
                <Route path="/leads/triage" element={<LeadTriageDashboard />} />
                <Route path="/leads/dashboard" element={<LeadTriageDashboard />} />
                {/* SO-PO Orchestration */}
                <Route path="/so-po-mapping" element={<SoPoMappingPage />} />
                <Route path="/sales-orders/:soId/margins" element={<MarginCalculatorPage />} />
                <Route path="/sales-orders/:soId/financial-approval" element={<FinancialApprovalPage />} />
                {/* Service */}
                <Route path="/service/sop-dashboard" element={<ServiceSOPDashboardPage />} />
                <Route path="/service/complaints/:id" element={<ComplaintDetailPage />} />
                {/* ITBP SWH Installation Flow */}
                <Route path="/service/itbp-installations" element={<ITBPInstallationsPage />} />
                <Route path="/service/itbp-installations/:id" element={<InstallationJobDetailPage />} />
                <Route path="/service/itbp-reports" element={<ITBPReportsPage />} />
                <Route path="/service-tickets" element={<ServiceTicketsPage />} />
                <Route path="/service-tickets/new" element={<ServiceTicketCreatePage />} />
                <Route path="/service-tickets/:id" element={<ServiceTicketDetailPage />} />
                <Route path="/service-tickets/:id/components" element={<ComponentIssuePage />} />
                <Route path="/service-tickets/:id/photos" element={<ServicePhotosPage />} />
                <Route path="/service-tickets/:id/audit" element={<ServiceAuditPage />} />
                <Route path="/service-tickets/:id/otp" element={<OtpVerifyPage />} />
                <Route path="/inventory/units/:code/history" element={<ProductUnitWarrantyHistoryPage />} />
                <Route path="/support/tickets" element={<SupportTicketsPage />} />
                <Route path="/support/tickets/:id" element={<SupportTicketDetailPage />} />
                <Route path="/products/:id/docs" element={<ProductDocsAdminPage />} />
                <Route path="/service/intake" element={<ComplaintIntakePage />} />
                <Route path="/service/warranty-verify/:id" element={<WarrantyVerificationPage />} />
                <Route path="/service/pre-visit/:id" element={<PreVisitChecklistPage />} />
                <Route path="/service/diagnosis/:id" element={<ProductDiagnosisPage />} />
                <Route path="/service/on-site/:id" element={<OnSiteExecutionPage />} />
                <Route path="/service/warranty-claim/:id" element={<WarrantyClaimPage />} />
                <Route path="/warranties" element={<WarrantiesPage />} />
                {/* HR */}
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/leave-requests" element={<LeavePage />} />
                <Route path="/incentives" element={<IncentivesPage />} />
                {/* Automation */}
                <Route path="/workflows" element={<WorkflowsPage />} />
                {/* Analytics */}
                <Route path="/analytics" element={<AnalyticsPage />} />
                {/* AI */}
                <Route path="/ai" element={<AIAssistantPage />} />
                <Route path="/ai/agents" element={<AIAgentsPage />} />
                <Route path="/ai/integrations" element={<APIIntegrationsPage />} />
                <Route path="/credits" element={<CreditsPage />} />
                {/* Proposal Builder + Knowledge Base */}
                <Route path="/proposal-builder" element={<ProposalBuilderPage />} />
                <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                {/* Super Admin */}
                <Route path="/admin" element={<SuperAdminDashboard />} />
                <Route path="/admin/tenants" element={<TenantManagementPage />} />
                <Route path="/admin/billing" element={<BillingPage />} />
                <Route path="/admin/health" element={<SystemHealthPage />} />
                <Route path="/admin/team" element={<TeamPage />} />
                <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
                <Route path="/admin/login-history" element={<LoginHistoryPage />} />
                <Route path="/admin/resources" element={<ResourceManagement />} />
                {/* Apr 26, 2026 (Squad-B) — admin lead approval queue */}
                <Route path="/admin/lead-approvals" element={<AdminLeadApprovalsPage />} />
                {/* Apr 30, 2026 (Squad-H) — tenant config admin settings */}
                <Route path="/admin/settings" element={<StrictViewBlock><AdminSettingsPage /></StrictViewBlock>} />
                {/* CR-D (2026-05-01) — carrier virtual-number assignment management */}
                <Route path="/admin/carrier-numbers" element={<CarrierNumbersPage />} />
                {/* Phase BB3b (2026-05-23) — installation focus-box review/edit */}
                <Route path="/admin/installations" element={<InstallationsFocusPage />} />
                {/* Phase 5 (2026-06-19) — Veda Stage Master editor (VEDA_STAGE_MASTER_ENABLED flag; OFF at deploy → calm disabled state) */}
                <Route path="/admin/stage-master" element={<StageMasterEditorPage />} />
                {/* Company Profile — the seller-of-record on every document.
                    🔒 Route-gated, not merely nav-gated: hiding the link stops
                    it being FOUND, never being TYPED. The role list matches the
                    server's tenantAdminOnly exactly. */}
                <Route
                  path="/admin/company-profile"
                  element={
                    <RequireRoles roles={['admin', 'owner']}>
                      <CompanyProfilePage />
                    </RequireRoles>
                  }
                />
                {/* Track D (May 2026, Ananya) — Sun Stellar Pool Heat Pump Calculator.
                    Backend gates everything behind `pool_heat_pump_calculator` in
                    tenant.enabledModules + meetingSessionToken — direct URL access
                    falls back to the friendly Launcher screen. */}
                <Route path="/sales/meeting/start" element={<MeetingModeLauncher />} />
                {/* Track D-CHOOSER (May 2026) — intermediate Calculator Chooser
                    page between lead pick and the actual calculator. Lists all
                    calculators enabled for the tenant. */}
                <Route path="/sales/meeting/calculators" element={<CalculatorChooserPage />} />
                <Route path="/sales/meeting/calculator" element={<PoolCalculatorPage />} />
                {/* Track D Calculator #1 (May 2026 reference-port Task 3.6) —
                    Pool Heat Pump calculator (forward + reverse flow). Alias
                    of `/sales/meeting/calculator` so the chooser & launcher
                    links stay deep-linkable. */}
                <Route path="/sales/meeting/calculator/pool" element={<PoolCalculatorPage />} />
                {/* Track D Calculator #2 (May 2026) — Sun Stellar Commercial Heat
                    Pump Calculator. Backend at /v1/commercial-heat-pump/sizing,
                    gated by `commercial_heat_pump_calculator` module flag +
                    meeting session token. */}
                <Route
                  path="/sales/meeting/calculator/commercial-heat-pump"
                  element={<CommercialHeatPumpCalculatorPage />}
                />
                {/* Track D Calculator #3 (May 2026) — Sun Stellar SS Water Tank
                    Calculator. Backend at /v1/ss-tank/sizing, gated by
                    `ss_tank_calculator` module flag + meeting session token.
                    Auto-surfaces in chooser once ST-B registers it in
                    /calculators registry. */}
                <Route
                  path="/sales/meeting/calculator/ss-tank"
                  element={<SsTankCalculatorPage />}
                />
                {/* Track D Calculator #4 (May 2026) — Sun Stellar Domestic
                    Heat Pump (water heating) Calculator. Backend at
                    /v1/domestic-heat-pump/sizing, gated by
                    `domestic_heat_pump_calculator` module flag + meeting
                    session token. Auto-surfaces in chooser once DHP-B
                    registers it in /calculators registry. */}
                <Route
                  path="/sales/meeting/calculator/domestic-heat-pump"
                  element={<DomesticHeatPumpCalculatorPage />}
                />
                <Route path="/admin/pool-models" element={<PoolModelsAdmin />} />
                <Route path="/admin/commercial-heat-pump-models" element={<CommercialHeatPumpModelsAdmin />} />
                <Route path="/admin/ss-tank-models" element={<SsTankModelsAdmin />} />
                <Route path="/admin/domestic-heat-pump-models" element={<DomesticHeatPumpModelsAdmin />} />
                {/* Telecaller */}
                <Route path="/telecaller" element={<TelecallerTodayPage />} />
                <Route path="/telecaller/call/:callId" element={<CallWorkspacePage />} />
                <Route path="/call-logs" element={<CallLogsPage />} />
                {/* ChatKart Call Handoff — agent call history (M4) */}
                <Route path="/calls" element={<MyCallsPage />} />
                <Route path="/offers" element={<ProposalTrackingPage />} />
                <Route path="/team-performance" element={<TeamPerformancePage />} />
                {/* Sales Manager dual-tab pages (Apr 22, 2026 exec sprint — combines /tasks + /team-performance with ?tab=personal|team) */}
                <Route path="/sales/activities" element={<ActivitiesDualPage />} />
                <Route path="/sales/reps" element={<RepsDualPage />} />
                {/* Executive sub-pages (Apr 22, 2026 exec sprint — 14 pages) */}
                <Route path="/executive/sales/call-queue" element={<ExecCallQueuePage />} />
                <Route path="/executive/sales/deals" element={<ExecMyDealsPage />} />
                <Route path="/executive/sales/incentives" element={<ExecIncentivesPage />} />
                <Route path="/executive/sales/history" element={<ExecCallHistoryPage />} />
                <Route path="/executive/service/tickets" element={<ExecMyTicketsPage />} />
                <Route path="/executive/service/parts-expenses" element={<ExecPartsAndExpensesPage />} />
                <Route path="/executive/production/work-orders" element={<ExecMyWorkOrdersPage />} />
                <Route path="/executive/production/qc-checkpoints" element={<ExecMyQCCheckpointsPage />} />
                <Route path="/executive/warehouse/grn" element={<ExecGRNQueuePage />} />
                <Route path="/executive/warehouse/dispatch" element={<ExecMyDispatchPage />} />
                <Route path="/executive/accounts/match-queue" element={<ExecMatchQueuePage />} />
                <Route path="/executive/accounts/approvals" element={<ExecApprovalsPage />} />
                <Route path="/executive/field-partner/claims" element={<ExecMyClaimsPage />} />
                <Route path="/executive/field-partner/payouts" element={<ExecMyPayoutsPage />} />
                {/* Profile */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/account" element={<Navigate to="/profile" replace />} />
                {/* Settings — strict-view roles (lead_triage_coordinator) redirected away */}
                {/* AI Assistant rename (Enterprise-only) — must come before wildcard */}
                <Route path="/settings/ai-assistant" element={<StrictViewBlock><AIAssistantSettingsPage /></StrictViewBlock>} />
                {/* Sessions & Devices (2026-09-05). Admin authority is enforced
                    server-side: every /sessions/admin/* endpoint is behind
                    requireRole. 🔒 StrictViewBlock is NOT that gate — it is the
                    strict-view exclusion, and these two routes sit ABOVE
                    /settings/*, so without it a lead_triage_coordinator reaches
                    two /settings/… URLs that every sibling redirects her away
                    from. */}
                <Route path="/settings/sessions" element={<StrictViewBlock><SessionsDevicesPage /></StrictViewBlock>} />
                <Route path="/settings/sessions/:userId" element={<StrictViewBlock><UserDeviceHistoryPage /></StrictViewBlock>} />
                <Route path="/settings/*" element={<StrictViewBlock><SettingsPage /></StrictViewBlock>} />
                <Route path="/billing/plans" element={<BillingPlansPage />} />

                {/* ── Lead Manager (Apr 28, 2026 — Squad-D, 12 screens) ── */}
                <Route path="/lead-manager" element={<LeadManagerHome />} />
                <Route path="/lead-manager/unverified" element={<LMUnverifiedQueuePage />} />
                <Route path="/lead-manager/verify/:id" element={<VerifyLeadPage />} />
                <Route path="/lead-manager/verified" element={<LMVerifiedPoolPage />} />
                <Route path="/lead-manager/assigned" element={<AssignedOversightPage />} />
                <Route path="/lead-manager/reassignments" element={<ReassignmentOutboxPage />} />
                <Route path="/lead-manager/metrics" element={<LeadMetricsPage />} />
                <Route path="/lead-manager/search" element={<LeadSearchPage />} />
                <Route path="/lead-manager/leads/:id" element={<LeadDetailPage />} />
                <Route path="/lead-manager/settings" element={<LeadManagerSettingsPage />} />
                {/* /admin/lead-approvals already wired by Squad-B above; the
                    component body has been upgraded to Squad-D's richer card
                    queue, so the existing route now renders the new UI. */}

                {/* ── Route aliases for common mistyped paths (Apr 23, 2026 Agent 4)
                       QA reported blank screens on these — they're not in the
                       sidebar but users (and external doc links) try them. We
                       redirect to the actual registered routes. ── */}
                <Route path="/warehouse" element={<Navigate to="/warehouse/dashboard" replace />} />
                <Route path="/warehouse/inventory" element={<Navigate to="/inventory" replace />} />
                <Route path="/service" element={<Navigate to="/service-tickets" replace />} />
                <Route path="/service/tickets" element={<Navigate to="/service-tickets" replace />} />
                <Route path="/tickets" element={<Navigate to="/service-tickets" replace />} />

                {/* Catch-all 404 — prevents silent blank screens on any future
                    unregistered route. MUST be last. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </RoutedErrorBoundary>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
